import { useState, useEffect } from "react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { useToastMutation } from "@/hooks/useToastMutation";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Spinner } from "@/components/ui/spinner";
import {
  UserPlus,
  Mail,
  CheckCircle2,
  Users,
  ArrowRight,
  ArrowLeft,
  Search,
  Copy,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useLocalPagination } from "@/hooks/useLocalPagination";

interface CompanyUsersAssignmentWizardProps {
  isOpen: boolean;
  onClose: () => void;
  companyId: Id<"companies">;
  companyName: string;
  onSuccess?: () => void;
}

type UserWithRole = Doc<"users"> & { role?: Doc<"roles"> | null };

interface UserAssignment {
  user: UserWithRole;
  roleId: Id<"roles">;
  sendInvitation: boolean;
}

type WizardStep =
  | "select"
  | "assign-roles"
  | "confirm"
  | "processing"
  | "success";

export default function CompanyUsersAssignmentWizard({
  isOpen,
  onClose,
  companyId,
  companyName,
  onSuccess,
}: CompanyUsersAssignmentWizardProps) {
  const [currentStep, setCurrentStep] = useState<WizardStep>("select");
  const [selectedUsers, setSelectedUsers] = useState<Set<Id<"users">>>(
    new Set(),
  );
  const [userAssignments, setUserAssignments] = useState<
    Map<Id<"users">, UserAssignment>
  >(new Map());
  const [searchQuery, setSearchQuery] = useState("");
  const [progress, setProgress] = useState(0);
  const [processedCount, setProcessedCount] = useState(0);
  const [invitationLinks, setInvitationLinks] = useState<
    Array<{ email: string; link: string }>
  >([]);
  const [copiedEmails, setCopiedEmails] = useState<Set<string>>(new Set());

  // Hook de paginación local
  const {
    data: usersWithoutCompany,
    currentPage,
    totalPages,
    isLoading,
    hasNextPage,
    hasPreviousPage,
    goToPage,
    nextPage,
    previousPage,
    goToFirstPage,
    updateFilters,
  } = useLocalPagination({
    queryFunction: api.system.users.queries.getUsersWithoutCompany,
    initialFilters: { search: searchQuery },
    pageSize: 10,
    initialPage: 1,
  });

  const allRoles = useQuery(api.system.roles.queries.getAllRoles);

  const { mutate: assignUser } = useToastMutation(
    api.system.users.mutations.assignUserToCompany,
  );

  const { mutate: sendInvitation } = useToastMutation(
    api.system.users.mutations.prepareResendInvitation,
  );

  const companyRoles =
    allRoles?.filter(
      (role: Doc<"roles">) =>
        role.type === "company_admin" || role.type === "company_user",
    ) || [];

  const defaultRole = companyRoles.find(
    (role: Doc<"roles">) => role.type === "company_user",
  );

  // Actualizar filtros cuando cambia la búsqueda
  useEffect(() => {
    updateFilters({ search: searchQuery });
  }, [searchQuery, updateFilters]);

  const handleUserToggle = (user: UserWithRole) => {
    const newSelected = new Set(selectedUsers);
    if (newSelected.has(user._id)) {
      newSelected.delete(user._id);
      const newAssignments = new Map(userAssignments);
      newAssignments.delete(user._id);
      setUserAssignments(newAssignments);
    } else {
      newSelected.add(user._id);
      if (defaultRole) {
        const newAssignments = new Map(userAssignments);
        newAssignments.set(user._id, {
          user,
          roleId: defaultRole._id,
          sendInvitation: true,
        });
        setUserAssignments(newAssignments);
      }
    }
    setSelectedUsers(newSelected);
  };

  const handleRoleChange = (userId: Id<"users">, roleId: Id<"roles">) => {
    const assignment = userAssignments.get(userId);
    if (assignment) {
      const newAssignments = new Map(userAssignments);
      newAssignments.set(userId, { ...assignment, roleId });
      setUserAssignments(newAssignments);
    }
  };

  const handleInvitationToggle = (userId: Id<"users">) => {
    const assignment = userAssignments.get(userId);
    if (assignment) {
      const newAssignments = new Map(userAssignments);
      newAssignments.set(userId, {
        ...assignment,
        sendInvitation: !assignment.sendInvitation,
      });
      setUserAssignments(newAssignments);
    }
  };

  const handleNext = () => {
    if (currentStep === "select" && selectedUsers.size > 0) {
      setCurrentStep("assign-roles");
    } else if (currentStep === "assign-roles") {
      setCurrentStep("confirm");
    } else if (currentStep === "confirm") {
      processAssignments();
    }
  };

  const handleBack = () => {
    if (currentStep === "assign-roles") {
      setCurrentStep("select");
    } else if (currentStep === "confirm") {
      setCurrentStep("assign-roles");
    }
  };

  const processAssignments = async () => {
    setCurrentStep("processing");
    setProgress(0);
    setProcessedCount(0);

    const assignments = Array.from(userAssignments.values());
    const total = assignments.length;
    const links: Array<{ email: string; link: string }> = [];

    for (let i = 0; i < assignments.length; i++) {
      const assignment = assignments[i];

      try {
        await assignUser({
          userId: assignment.user._id,
          companyId,
          roleId: assignment.roleId,
        });

        if (assignment.sendInvitation && assignment.user.email) {
          try {
            const result = await sendInvitation({
              userId: assignment.user._id,
            });

            if (result?.invitationUrl) {
              links.push({
                email: assignment.user.email,
                link: result.invitationUrl,
              });
            }
          } catch (invError) {
            console.error("Error sending invitation:", invError);
          }
        }

        setProcessedCount(i + 1);
        setProgress(((i + 1) / total) * 100);
      } catch (error) {
        console.error(`Error assigning user ${assignment.user.email}:`, error);
        toast.error(
          `Error al asignar ${assignment.user.email}: ${error instanceof Error ? error.message : "Error desconocido"}`,
        );
      }
    }

    setInvitationLinks(links);
    setCurrentStep("success");

    if (onSuccess) {
      onSuccess();
    }
  };

  const copyInvitationLink = (email: string, link: string) => {
    navigator.clipboard.writeText(link);
    setCopiedEmails(new Set(copiedEmails).add(email));
    toast.success(`Link copiado para ${email}`);

    setTimeout(() => {
      setCopiedEmails((prev) => {
        const newSet = new Set(prev);
        newSet.delete(email);
        return newSet;
      });
    }, 2000);
  };

  const handleClose = () => {
    setCurrentStep("select");
    setSelectedUsers(new Set());
    setUserAssignments(new Map());
    setSearchQuery("");
    goToFirstPage();
    setProgress(0);
    setProcessedCount(0);
    setInvitationLinks([]);
    setCopiedEmails(new Set());
    onClose();
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "select":
        return (
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Search className="h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar por nombre o email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex-1"
              />
            </div>

            <ScrollArea className="h-100 border rounded-lg">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Spinner className="h-6 w-6" />
                </div>
              ) : usersWithoutCompany.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center p-8">
                  <Users className="h-12 w-12 text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    No hay usuarios sin empresa asignada
                  </p>
                </div>
              ) : (
                <div className="p-4 space-y-2">
                  {usersWithoutCompany.map((user: UserWithRole) => (
                    <div
                      key={user._id}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:bg-accent transition-colors cursor-pointer"
                      onClick={() => handleUserToggle(user)}
                    >
                      <Checkbox
                        checked={selectedUsers.has(user._id)}
                        onCheckedChange={() => handleUserToggle(user)}
                      />
                      <div className="flex-1">
                        <div className="font-medium">{user.name}</div>
                        <div className="text-sm text-muted-foreground">
                          {user.email}
                        </div>
                      </div>
                      {user.role && (
                        <Badge variant="secondary">{user.role.name}</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>

            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>{selectedUsers.size} usuario(s) seleccionado(s)</span>
              {usersWithoutCompany && (
                <span>
                  Página {currentPage} de {totalPages}
                </span>
              )}
            </div>

            {/* Controles de paginación */}
            {usersWithoutCompany && totalPages > 1 && (
              <div className="flex items-center justify-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={previousPage}
                  disabled={!hasPreviousPage || isLoading}
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">
                  Página {currentPage} de {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={nextPage}
                  disabled={!hasNextPage || isLoading}
                >
                  <ArrowRight className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        );

      case "assign-roles":
        return (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Asigna roles a cada usuario seleccionado
            </p>

            <ScrollArea className="h-100 border rounded-lg">
              <div className="p-4 space-y-4">
                {Array.from(userAssignments.values()).map((assignment) => (
                  <div
                    key={assignment.user._id}
                    className="flex flex-col gap-3 p-4 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="font-medium">
                          {assignment.user.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {assignment.user.email}
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label>Rol</Label>
                        <Select
                          value={assignment.roleId}
                          onValueChange={(value) =>
                            handleRoleChange(
                              assignment.user._id,
                              value as Id<"roles">,
                            )
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {companyRoles.map((role) => (
                              <SelectItem key={role._id} value={role._id}>
                                {role.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label>Invitación</Label>
                        <div className="flex items-center gap-2 h-10">
                          <Checkbox
                            id={`invitation-${assignment.user._id}`}
                            checked={assignment.sendInvitation}
                            onCheckedChange={() =>
                              handleInvitationToggle(assignment.user._id)
                            }
                          />
                          <Label
                            htmlFor={`invitation-${assignment.user._id}`}
                            className="text-sm font-normal cursor-pointer"
                          >
                            Enviar invitación
                          </Label>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        );

      case "confirm": {
        const invitationsToSend = Array.from(userAssignments.values()).filter(
          (a) => a.sendInvitation,
        ).length;

        return (
          <div className="space-y-4">
            <div className="p-4 bg-muted rounded-lg space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Empresa:</span>
                <span className="text-sm">{companyName}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Usuarios a asignar:</span>
                <Badge>{userAssignments.size}</Badge>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">
                  Invitaciones a enviar:
                </span>
                <Badge variant="secondary">{invitationsToSend}</Badge>
              </div>
            </div>

            <ScrollArea className="h-75 border rounded-lg">
              <div className="p-4 space-y-3">
                {Array.from(userAssignments.values()).map((assignment) => {
                  const role = companyRoles.find(
                    (r) => r._id === assignment.roleId,
                  );
                  return (
                    <div
                      key={assignment.user._id}
                      className="flex items-center justify-between p-3 border rounded-lg"
                    >
                      <div className="flex-1">
                        <div className="font-medium">
                          {assignment.user.name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {assignment.user.email}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge>{role?.name}</Badge>
                        {assignment.sendInvitation && (
                          <Mail className="h-4 w-4 text-primary" />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>

            <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
              <p className="text-sm text-center">
                ¿Confirmar asignación de {userAssignments.size} usuario(s) a{" "}
                <strong>{companyName}</strong>?
              </p>
            </div>
          </div>
        );
      }

      case "processing":
        return (
          <div className="space-y-6 py-8">
            <div className="flex flex-col items-center gap-4">
              <Spinner className="h-12 w-12" />
              <div className="text-center">
                <h3 className="text-lg font-medium">
                  Procesando asignaciones...
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {processedCount} de {userAssignments.size} usuarios procesados
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-center text-muted-foreground">
                {Math.round(progress)}% completado
              </p>
            </div>
          </div>
        );

      case "success":
        return (
          <div className="space-y-6 py-4">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-medium">¡Asignación completada!</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {userAssignments.size} usuario(s) asignado(s) exitosamente a{" "}
                  {companyName}
                </p>
              </div>
            </div>

            {invitationLinks.length > 0 && (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Mail className="h-4 w-4 text-muted-foreground" />
                  <h4 className="text-sm font-medium">
                    Links de invitación generados:
                  </h4>
                </div>

                <ScrollArea className="h-50 border rounded-lg">
                  <div className="p-3 space-y-2">
                    {invitationLinks.map(({ email, link }) => (
                      <div
                        key={email}
                        className="flex items-center gap-2 p-2 bg-muted rounded"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium">{email}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {link}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyInvitationLink(email, link)}
                        >
                          {copiedEmails.has(email) ? (
                            <Check className="h-4 w-4 text-primary" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  const getStepTitle = () => {
    switch (currentStep) {
      case "select":
        return "Seleccionar Usuarios";
      case "assign-roles":
        return "Asignar Roles";
      case "confirm":
        return "Confirmar Asignación";
      case "processing":
        return "Procesando";
      case "success":
        return "Completado";
      default:
        return "";
    }
  };

  const getStepDescription = () => {
    switch (currentStep) {
      case "select":
        return `Selecciona los usuarios que deseas asignar a ${companyName}`;
      case "assign-roles":
        return "Define el rol y si enviar invitación para cada usuario";
      case "confirm":
        return "Revisa y confirma la información antes de proceder";
      case "processing":
        return "No cierres esta ventana hasta que termine el proceso";
      case "success":
        return "Los usuarios han sido asignados exitosamente";
      default:
        return "";
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case "select":
        return selectedUsers.size > 0;
      case "assign-roles":
        return true;
      case "confirm":
        return true;
      default:
        return false;
    }
  };

  return (
    <Dialog
      open={isOpen}
      onOpenChange={
        currentStep === "success" || currentStep === "processing"
          ? undefined
          : handleClose
      }
    >
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            {getStepTitle()}
          </DialogTitle>
          <DialogDescription>{getStepDescription()}</DialogDescription>
        </DialogHeader>

        <div className="py-4">{renderStepContent()}</div>

        {currentStep !== "processing" && (
          <DialogFooter className="gap-2">
            {currentStep === "success" ? (
              <Button onClick={handleClose} className="w-full">
                Cerrar
              </Button>
            ) : (
              <>
                {currentStep !== "select" && (
                  <Button variant="outline" onClick={handleBack}>
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Atrás
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  disabled={!canProceed()}
                  className="flex-1"
                >
                  {currentStep === "confirm" ? (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Confirmar Asignación
                    </>
                  ) : (
                    <>
                      Siguiente
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}
